<template>
  <div class="cafe poster" :class="{ usersHovered: hovered.size > 0 }">
    <transition name="slowFade">
      <div class="wall poster" v-if="hasWall"></div>
    </transition>
    <transition name="slowFade">
      <div class="propsFrame poster" v-if="afterMeetingSpaceCard">
        <avatars-sofa />
        <transition name="basic">
          <div v-if="hasCoffeeTable" class="coffeeTable" :style="tableStyle"></div>
        </transition>
        <transition name="basic">
          <div v-if="hasGridBg" class="gridBg">
            <div class="sofa"></div>
            <div class="table"></div>
            <div class="plant"></div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useFrameStore } from '@/store/pinia/frame';
import { usePhaseStore } from '@/store/pinia/phase';
import { useUsersStore } from '@/store/pinia/users';
import { useReady } from '@/composables/useReady';
import AvatarsSofa from '@/components/AvatarsSofa';
const frameStore = useFrameStore();
const phaseStore = usePhaseStore();
const usersStore = useUsersStore();

const { afterMeetingSpaceCard } = useReady();

const { positions: userPositions, positionDetails, usersVisible, iAmAway, iAmInWaitingRoom, myId, hovered } = storeToRefs(usersStore);
const { currentPhase, isPresentation } = storeToRefs(phaseStore);
const { contHeight } = storeToRefs(frameStore);

const hasCoffeeTable = computed(() => positionDetails.value?.layoutName === 'couch' || iAmAway.value || iAmInWaitingRoom.value);
const hasGridBg = computed(() => {
  if (positionDetails.value?.layoutName === 'couch' || iAmAway.value || iAmInWaitingRoom.value) {
    return false;
  } else if (['waiting', 'lobby', 'endMeeting', 'finish', 'ended', 'input'].includes(currentPhase.value) || isPresentation.value) {
    return false;
  } else {
    return true;
  }
});

const hasWall = computed(() => {
  if (usersVisible.value.length < 3) {
    return false;
  }
  if (['waiting', 'lobby', 'endMeeting', 'finish', 'ended'].includes(currentPhase.value)) {
    return false;
  }
  return true;
});

const wallTop = computed(() => {
  if (positionDetails.value?.layoutName === 'couch' || iAmAway.value || iAmInWaitingRoom.value) {
    return positionDetails.value.bottom + frameStore.pxToRem(35) + 'px';
  }
  return contHeight.value - frameStore.pxToRem(255) + 'px';
});

const tableStyle = computed(() => {
  let css = {
    top: positionDetails.value.bottom - frameStore.pxToRem(35) + 'px',
    bottom: 'auto',
  };

  if (iAmInWaitingRoom.value) {
    const { w, left } = userPositions.value[myId.value];
    css.width = w + frameStore.pxToRem(20) + 'px';
    css.left = left + w / 2 + 'px';
    css.bottom = 'auto';
  }

  if (iAmAway.value) {
    css.left = '70%';
  }
  return css;
});
</script>
<style lang="scss" scoped>
.wall {
  position: absolute;
  z-index: 0;
  bottom: 0;
  top: v-bind(wallTop);
  width: 100%;
  background-image: linear-gradient(to bottom, var(--c__bg), var(--c__bg-0) rem(255px));
  transition: all 0.5s cubic-bezier(0.45, 0, 0.55, 1);
  @include transition-optional;
}

@property --_bottleFade {
  syntax: '<percentage>'; /* <- defined as type number for the transition to work */
  initial-value: 99.99%;
  inherits: false;
}

.coffeeTable {
  position: absolute;
  left: 50%;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center bottom;
  background-image: url($base-url + '/assets/images/spaces/cafe/coffee-table.png');
  z-index: 100;
  width: 100%;
  pointer-events: none;
  transform: translateX(-50%);
  height: rem(262px);
  width: rem(282px);
  display: block;
  transition: all 0.5s cubic-bezier(0.45, 0, 0.55, 1);
  mask-image: linear-gradient(to top, rgba(0, 0, 0, 1) var(--_bottleFade), rgba(0, 0, 0, 0.05) 100%);
  transition: --_bottleFade 1s;
  --_bottleFade: 99.999%;
  .usersHovered & {
    --_bottleFade: 50%;
  }

  &:before {
    position: absolute;
    content: '';
    bottom: 0px;
    height: 5px;
    background: var(--c__shadow-opaque);
    box-shadow: 0 0 10px var(--c__shadow-opaque), 0 0 5px var(--c__shadow-opaque), 0 0 3px var(--c__shadow-opaque);
    left: -1em;
    right: -1em;
    border-radius: 50%;
  }

  @include transition-optional;
}

.gridBg {
  position: absolute;
  width: 100%;
  bottom: rem(250px);
  top: 0;
  flex-direction: row;
  display: flex;
  justify-content: space-around;
  align-items: stretch;
  gap: 1em;
  & > div {
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center bottom;
  }
  .sofa {
    flex: 0 1 rem(435px);
    background-image: url($base-url + '/assets/images/spaces/cafe/sofa-basic.svg');
  }
  .table {
    flex: 0 1 rem(197px);
    background-image: url($base-url + '/assets/images/spaces/cafe/coffee-table.svg');
    margin: auto 0 0 0;
  }
  .plant {
    flex: 0 1 rem(188px);
    background-image: url($base-url + '/assets/images/spaces/cafe/potplant.svg');
  }
}

.propsFrame {
  @include sidebar-response;
}
</style>
